import { Chess, type Move } from 'chess.js';
import { Chessground } from '@lichess-org/chessground';
import type { Api } from '@lichess-org/chessground/api';
import type { Color, Key } from '@lichess-org/chessground/types';
import {
  chessDests,
  chessTurnColor,
  isEngineTurnToPlay,
  formatMoveList,
  gameStatusText,
  needsPromotion,
  oppositeColor,
} from '../utils/chessGroundBridge';
import { evalBarPresentation, formatEvalScore, type EvalScore } from '../utils/chessEval';
import {
  CHESS_EVAL_BAR_MOVETIME_MS,
  CHESS_EVAL_REVIEW_AFTER_MS,
  CHESS_EVAL_REVIEW_BEST_MS,
} from '../utils/chessEngineConfig';
import {
  buildMoveReviewResult,
  moveReviewLabelText,
  uciBestMoveSan,
  verboseMoveToUci,
  type MoveReviewResult,
} from '../utils/chessMoveReview';
import { askChessCoach } from '../services/chessCoachApi';
import {
  buildAnalyzeGamePrompt,
  buildExplainPositionPrompt,
} from '../utils/chessCoachPrompts';
import {
  coachLoadingHtml,
  coachReplyHtml,
  mountChessCoachModals,
  openChessCoachResultModal,
  promptChessAnalyzeGame,
  setChessCoachResultBody,
} from './chessCoachModal';
import { ChessStockfish, uciToMove } from '../utils/chessStockfish';
import {
  clearChessDebugEntries,
  formatChessDebugLogText,
  getChessDebugEntries,
  initChessDebug,
  isChessDebugEnabled,
  logChessDebug,
} from '../utils/chessDebug';
import { flashCopyOnButton } from '../utils/copyFeedback';
import { copyTextToClipboard } from '../utils/formatListClipboard';
import { escapeHtml } from '../utils/escapeHtml';
import { appAlert } from '../utils/appDialog';
import { loadCursorAgentPreferences } from '../utils/cursorAgentPreferences';
import { loadLichessPreferences } from '../utils/lichessPreferences';
import {
  fetchLichessAccount,
  fetchLichessGamePgn,
  fetchRecentLichessGames,
  pgnFromLichessGame,
} from '../services/lichessApi';
import { summarizeLichessGame, type LichessGameSummary } from '../utils/lichessGameSummary';
import { positionAfterMoves, sanMovesFromPgn } from '../utils/chessReplay';
import type { ChessPlayerColor } from '../utils/chessPgnMeta';

export type ChessViewDeps = {
  saveAnalyzedGame: (opts: {
    pgn: string;
    analysis: string;
    playerColor: ChessPlayerColor | null;
    headline?: string;
  }) => Promise<void>;
  chessGamesPath: string;
  onJournalSaved?: () => void;
};

import '@lichess-org/chessground/assets/chessground.base.css';
import '@lichess-org/chessground/assets/chessground.brown.css';
import '@lichess-org/chessground/assets/chessground.cburnett.css';
import '../chessBoardCoords.css';

export type ChessPlayMode = 'local' | 'engine-white' | 'engine-black';

const PROMO_PIECES: { role: 'q' | 'r' | 'b' | 'n'; label: string }[] = [
  { role: 'q', label: 'Queen' },
  { role: 'r', label: 'Rook' },
  { role: 'b', label: 'Bishop' },
  { role: 'n', label: 'Knight' },
];

/** Full-featured chess: Chessground UI, chess.js rules, optional Stockfish opponent. */
export class ChessView {
  private chess = new Chess();
  private ground: Api | null = null;
  private engine = new ChessStockfish();
  private container: HTMLElement | null = null;
  private boardEl: HTMLElement | null = null;
  private orientation: Color = 'white';
  private playMode: ChessPlayMode = 'engine-white';
  private skillLevel = 4;
  private thinking = false;
  /** Blocks human moves while Stockfish reviews the last played move (vs engine). */
  private reviewingMove = false;
  private pendingPromotion: { orig: Key; dest: Key } | null = null;
  private evalGeneration = 0;
  private engineGeneration = 0;
  private reviewGeneration = 0;
  private lastEvalScore: EvalScore | null = null;
  private coachBusy = false;
  private deps: ChessViewDeps | null = null;
  private boardMode: 'play' | 'replay' = 'play';
  private boardCoordsObserver: ResizeObserver | null = null;
  private replayMoves: string[] = [];
  private replayIndex = 0;
  private replayTitle = '';
  private lichessUserId: string | null = null;
  private lichessSummaries = new Map<string, LichessGameSummary>();
  private lichessPgns = new Map<string, string>();

  render(container: HTMLElement, deps: ChessViewDeps): void {
    this.deps = deps;
    initChessDebug();
    mountChessCoachModals();
    this.dispose();
    this.engine = new ChessStockfish();
    this.container = container;
    this.chess = new Chess();
    this.orientation = 'white';
    this.thinking = false;
    this.reviewingMove = false;
    this.pendingPromotion = null;
    this.evalGeneration = 0;

    const levelOptions = Array.from({ length: 10 }, (_, i) => {
      const n = i + 1;
      return `<option value="${n}"${n === this.skillLevel ? ' selected' : ''}>Level ${n}</option>`;
    }).join('');

    container.innerHTML = `
      <div class="chess-page">
        <header class="chess-header">
          <h2 class="file-title">Chess</h2>
        </header>
        <div class="chess-layout">
          <div class="chess-board-column">
            <div class="chess-board-with-eval">
              <div class="chess-eval-bar" id="chess-eval-bar" aria-label="Engine evaluation" title="Evaluation for the side at the bottom of the board">
                <div class="chess-eval-fill" id="chess-eval-fill" style="height: 50%"></div>
                <span class="chess-eval-label" id="chess-eval-label">0.0</span>
              </div>
              <div class="chess-board-wrap">
                <div class="chess-board cg-wrap" id="chess-board"></div>
                <div class="chess-promotion hidden" id="chess-promotion" role="dialog" aria-label="Choose promotion piece">
                  <p class="chess-promotion-title">Promote to</p>
                  <div class="chess-promotion-pieces" id="chess-promotion-pieces"></div>
                </div>
              </div>
            </div>
            <div class="chess-board-toolbar" aria-label="Board actions">
              <button type="button" class="btn-primary chess-board-toolbar-btn" id="chess-undo">Undo</button>
              <button type="button" class="btn-ghost-dark chess-board-toolbar-btn" id="chess-flip">Flip board</button>
            </div>
            <div class="chess-replay-toolbar hidden" id="chess-replay-toolbar" aria-label="Replay controls">
              <p class="chess-replay-meta" id="chess-replay-meta"></p>
              <p class="chess-replay-ply" id="chess-replay-ply">Move 0 / 0</p>
              <div class="chess-replay-controls" aria-label="Step through moves">
                <button type="button" class="btn-ghost-dark btn-sm chess-replay-step" id="chess-replay-start" title="Start">⏮</button>
                <button type="button" class="btn-ghost-dark btn-sm chess-replay-step" id="chess-replay-prev" title="Previous move">◀</button>
                <button type="button" class="btn-ghost-dark btn-sm chess-replay-step" id="chess-replay-next" title="Next move">▶</button>
                <button type="button" class="btn-ghost-dark btn-sm chess-replay-step" id="chess-replay-end" title="End">⏭</button>
              </div>
              <button type="button" class="btn-ghost-dark btn-sm chess-replay-exit" id="chess-replay-exit">Play mode</button>
            </div>
            <p class="chess-move-review hidden" id="chess-move-review" aria-live="polite"></p>
            <p class="chess-thinking hidden" id="chess-thinking" aria-live="polite">Engine thinking…</p>
            <div class="chess-debug-panel hidden" id="chess-debug-panel" aria-live="polite">
              <div class="chess-debug-header">
                <span>Chess debug</span>
                <div class="chess-debug-actions">
                  <button type="button" class="btn-ghost-dark btn-sm" id="chess-debug-copy">Copy</button>
                  <button type="button" class="btn-ghost-dark btn-sm" id="chess-debug-clear">Clear</button>
                </div>
              </div>
              <pre class="chess-debug-log" id="chess-debug-log"></pre>
            </div>
          </div>
          <aside class="chess-panel" aria-label="Game controls">
            <p class="chess-status" id="chess-status"></p>
            <label class="chess-field">
              <span class="chess-field-label">Mode</span>
              <select id="chess-mode" class="chess-select">
                <option value="local">Two players (same device)</option>
                <option value="engine-white"${this.playMode === 'engine-white' ? ' selected' : ''}>You play White vs engine</option>
                <option value="engine-black"${this.playMode === 'engine-black' ? ' selected' : ''}>You play Black vs engine</option>
              </select>
            </label>
            <label class="chess-field">
              <span class="chess-field-label">Engine level</span>
              <select id="chess-skill" class="chess-select">${levelOptions}</select>
            </label>
            <div class="chess-actions">
              <button type="button" class="btn-primary" id="chess-new">New game</button>
            </div>
            <section class="chess-lichess-section" aria-labelledby="chess-lichess-heading">
              <div class="chess-lichess-header">
                <h3 class="chess-lichess-heading" id="chess-lichess-heading">My Lichess games</h3>
                <button type="button" class="btn-ghost-dark btn-sm" id="chess-lichess-refresh">Refresh</button>
              </div>
              <p class="chess-lichess-hint text-muted small" id="chess-lichess-hint">Add your Lichess token in Tools → Config.</p>
              <ul class="chess-lichess-list" id="chess-lichess-list" aria-live="polite"></ul>
            </section>
            <section class="chess-coach-section" aria-labelledby="chess-coach-heading">
              <h3 class="chess-coach-heading" id="chess-coach-heading">Coach (AI)</h3>
              <p class="chess-coach-hint text-muted small">Uses Cursor API key from Tools → Config.</p>
              <div class="chess-coach-actions">
                <button type="button" class="btn-ghost-dark" id="chess-explain-position">Explain position</button>
                <button type="button" class="btn-ghost-dark" id="chess-analyze-game">Analyze game</button>
              </div>
            </section>
            <section class="chess-moves-section" aria-labelledby="chess-moves-heading">
              <div class="chess-moves-header">
                <h3 class="chess-moves-heading" id="chess-moves-heading">Moves</h3>
                <button
                  type="button"
                  class="btn-icon chess-copy-pgn"
                  id="chess-copy-pgn"
                  title="Copy PGN"
                  aria-label="Copy PGN"
                >📋</button>
              </div>
              <ol class="chess-moves" id="chess-moves"></ol>
            </section>
            <p class="chess-credits">
              Board: <a href="https://github.com/lichess-org/chessground" target="_blank" rel="noopener noreferrer">Chessground</a>
              · Rules: <a href="https://github.com/jhlywa/chess.js" target="_blank" rel="noopener noreferrer">chess.js</a>
              · Engine: <a href="https://github.com/nmrugg/stockfish.js" target="_blank" rel="noopener noreferrer">Stockfish 18</a>
            </p>
          </aside>
        </div>
      </div>`;

    this.boardEl = container.querySelector<HTMLElement>('#chess-board');
    const promoPieces = container.querySelector<HTMLElement>('#chess-promotion-pieces');
    if (promoPieces) {
      promoPieces.innerHTML = PROMO_PIECES.map(
        p =>
          `<button type="button" class="chess-promo-btn" data-role="${p.role}" aria-label="${escapeHtml(p.label)}">${escapeHtml(p.label[0]!)}</button>`
      ).join('');
    }

    container.querySelector('#chess-mode')?.addEventListener('change', e => {
      this.playMode = (e.target as HTMLSelectElement).value as ChessPlayMode;
      this.newGame();
    });
    container.querySelector('#chess-skill')?.addEventListener('change', e => {
      this.skillLevel = Number((e.target as HTMLSelectElement).value);
    });
    container.querySelector('#chess-new')?.addEventListener('click', () => this.newGame());
    container.querySelector('#chess-undo')?.addEventListener('click', () => void this.undo());
    container.querySelector('#chess-flip')?.addEventListener('click', () => this.flipBoard());
    container.querySelector('#chess-copy-pgn')?.addEventListener('click', () => this.copyPgn());
    container.querySelector('#chess-explain-position')?.addEventListener('click', () => {
      void this.explainPosition();
    });
    container.querySelector('#chess-analyze-game')?.addEventListener('click', () => {
      void this.analyzeGame();
    });
    container.querySelector('#chess-lichess-refresh')?.addEventListener('click', () => {
      void this.refreshLichessGames();
    });
    container.querySelector('#chess-lichess-list')?.addEventListener('click', e => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-lichess-action]');
      if (!btn) return;
      const gameId = btn.dataset.gameId;
      const action = btn.dataset.lichessAction;
      if (!gameId || !action) return;
      if (action === 'load') void this.loadLichessGame(gameId);
      else if (action === 'analyze') void this.analyzeLichessGame(gameId);
    });
    container.querySelector('#chess-replay-start')?.addEventListener('click', () => this.setReplayIndex(0));
    container.querySelector('#chess-replay-prev')?.addEventListener('click', () =>
      this.setReplayIndex(this.replayIndex - 1)
    );
    container.querySelector('#chess-replay-next')?.addEventListener('click', () =>
      this.setReplayIndex(this.replayIndex + 1)
    );
    container.querySelector('#chess-replay-end')?.addEventListener('click', () =>
      this.setReplayIndex(this.replayMoves.length)
    );
    container.querySelector('#chess-replay-exit')?.addEventListener('click', () => this.exitReplayMode());

    promoPieces?.addEventListener('click', e => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-role]');
      if (!btn || !this.pendingPromotion) return;
      const role = btn.dataset.role as 'q' | 'r' | 'b' | 'n';
      const { orig, dest } = this.pendingPromotion;
      this.hidePromotion();
      this.applyUserMove(orig, dest, role);
    });

    this.mountChessDebugPanel(container);

    if (this.boardEl) {
      this.ground = Chessground(this.boardEl, this.buildConfig());
      this.installBoardCoordsFix();
      this.syncOrientationForMode();
      this.syncGround();
      this.updateUi();
      void this.requestEvaluation();
      if (this.engineMovesFirst()) void this.engineTurn();
    }
    void this.refreshLichessGames();
  }

  dispose(): void {
    this.evalGeneration++;
    this.engineGeneration++;
    this.thinking = false;
    this.reviewingMove = false;
    this.boardCoordsObserver?.disconnect();
    this.boardCoordsObserver = null;
    this.ground?.destroy();
    this.ground = null;
    this.engine.dispose();
    this.container = null;
    this.boardEl = null;
  }

  /** Human side when playing vs engine (matches mode labels). */
  private humanColor(): Color | null {
    if (this.playMode === 'local') return null;
    return this.playMode === 'engine-white' ? 'white' : 'black';
  }

  /** Engine moves first when human is Black. */
  private engineMovesFirst(): boolean {
    return this.playMode === 'engine-black';
  }

  private shouldEnginePlay(): boolean {
    return isEngineTurnToPlay(
      this.playMode,
      chessTurnColor(this.chess),
      this.humanColor(),
      this.chess.isGameOver()
    );
  }

  /** Cancel in-flight engine/review work so undo or a new search can run. */
  private cancelActiveSearches(): void {
    this.engineGeneration++;
    this.reviewGeneration++;
    this.evalGeneration++;
    this.engine.cancelPendingSearches();
    this.thinking = false;
    this.setThinkingVisible(false);
    this.setReviewingMove(false);
    this.container?.querySelector('#chess-move-review')?.classList.add('hidden');
  }

  private syncOrientationForMode(): void {
    const human = this.humanColor();
    if (human) {
      this.orientation = human;
      this.ground?.set({ orientation: human });
    }
  }

  private canHumanMove(): boolean {
    if (this.boardMode === 'replay') return false;
    if (this.thinking || this.reviewingMove || this.chess.isGameOver()) return false;
    const human = this.humanColor();
    if (!human) return true;
    return chessTurnColor(this.chess) === human;
  }

  private movableConfig(): { color: Color | 'both'; dests: ReturnType<typeof chessDests> } {
    const turn = chessTurnColor(this.chess);
    if (!this.canHumanMove()) {
      return { color: turn, dests: new Map() };
    }
    if (this.playMode === 'local') {
      return { color: turn, dests: chessDests(this.chess) };
    }
    const human = this.humanColor()!;
    return { color: human, dests: chessDests(this.chess) };
  }

  private buildConfig() {
    const movable = this.movableConfig();
    return {
      fen: this.chess.fen(),
      orientation: this.orientation,
      turnColor: chessTurnColor(this.chess),
      check: this.chess.inCheck(),
      coordinates: true,
      ranksPosition: 'left' as const,
      movable: {
        free: false,
        color: movable.color,
        dests: movable.dests,
        showDests: true,
        events: {
          after: (orig: Key, dest: Key) => {
            if (needsPromotion(this.chess, orig, dest)) {
              this.pendingPromotion = { orig, dest };
              this.showPromotion();
              this.syncGround();
              return;
            }
            this.applyUserMove(orig, dest);
          },
        },
      },
      draggable: { enabled: true, showGhost: true },
      selectable: { enabled: true },
      drawable: { enabled: true, visible: true },
      highlight: { lastMove: true, check: true },
      animation: { enabled: true, duration: 200 },
    };
  }

  /** Chessground file labels default to left:24px + width:100%, shifting letters ~one column. */
  private alignBoardCoords(): void {
    if (!this.boardEl) return;
    const files = this.boardEl.querySelector<HTMLElement>('coords.files');
    if (files) {
      files.style.setProperty('left', '0', 'important');
      files.style.setProperty('width', '100%', 'important');
      files.style.setProperty('right', 'auto', 'important');
    }
    const ranks = this.boardEl.querySelector<HTMLElement>('coords.ranks');
    if (ranks) {
      ranks.style.setProperty('top', '0', 'important');
      ranks.style.setProperty('left', '-17px', 'important');
    }
    const filesBottom = this.boardEl.querySelector<HTMLElement>('coords.files');
    if (filesBottom) {
      filesBottom.style.setProperty('bottom', '-15px', 'important');
    }
  }

  private mountChessDebugPanel(container: HTMLElement): void {
    const panel = container.querySelector<HTMLElement>('#chess-debug-panel');
    const logEl = container.querySelector<HTMLElement>('#chess-debug-log');
    if (!panel || !logEl) return;

    const renderLog = (): void => {
      if (!isChessDebugEnabled()) {
        panel.classList.add('hidden');
        return;
      }
      panel.classList.remove('hidden');
      logEl.textContent = formatChessDebugLogText(getChessDebugEntries()) || '(no events yet)';
    };

    renderLog();
    document.addEventListener('chess-debug', renderLog);
    container.querySelector('#chess-debug-copy')?.addEventListener('click', () => {
      const btn = container.querySelector<HTMLButtonElement>('#chess-debug-copy');
      const text = formatChessDebugLogText(getChessDebugEntries());
      if (!text) return;
      void copyTextToClipboard(text).then(
        () => {
          if (btn) flashCopyOnButton(btn, 'Copied');
        },
        () => {
          if (btn) flashCopyOnButton(btn, 'Copy failed');
        }
      );
    });
    container.querySelector('#chess-debug-clear')?.addEventListener('click', () => {
      clearChessDebugEntries();
      renderLog();
    });

    if (isChessDebugEnabled()) {
      logChessDebug('debug-ready', { hint: 'Engine and move-review events will appear here' });
      renderLog();
    }
  }

  private installBoardCoordsFix(): void {
    if (!this.boardEl) return;
    const apply = () => {
      this.alignBoardCoords();
      requestAnimationFrame(() => this.alignBoardCoords());
    };
    apply();
    this.boardCoordsObserver?.disconnect();
    this.boardCoordsObserver = new ResizeObserver(apply);
    this.boardCoordsObserver.observe(this.boardEl);
  }

  private syncGround(): void {
    if (!this.ground) return;
    const last = this.chess.history({ verbose: true });
    const movable = this.movableConfig();
    const patch: Parameters<Api['set']>[0] = {
      fen: this.chess.fen(),
      turnColor: chessTurnColor(this.chess),
      check: this.chess.inCheck(),
      movable: {
        color: movable.color,
        dests: movable.dests,
      },
      viewOnly: this.boardMode === 'replay' || this.chess.isGameOver(),
    };
    if (last.length) {
      const m = last[last.length - 1]!;
      patch.lastMove = [m.from as Key, m.to as Key];
    }
    this.ground.set(patch);
    this.alignBoardCoords();
  }

  private applyUserMove(orig: Key, dest: Key, promotion?: 'q' | 'r' | 'b' | 'n'): void {
    if (!this.canHumanMove()) {
      this.syncGround();
      return;
    }
    const fenBefore = this.chess.fen();
    try {
      const move = this.chess.move(
        promotion ? { from: orig, to: dest, promotion } : { from: orig, to: dest }
      );
      if (!move) {
        this.syncGround();
        return;
      }
      void this.afterMove({ fenBefore, lastMove: move });
    } catch {
      this.syncGround();
      return;
    }
  }

  private async afterMove(ctx?: {
    fenBefore: string;
    lastMove: Move;
  }): Promise<void> {
    this.syncGround();
    this.updateUi();

    const fenAfterUser = this.chess.fen();
    const gameOver = this.chess.isGameOver();
    const engineToPlay = this.shouldEnginePlay();

    logChessDebug('after-move', {
      engineToPlay,
      turn: chessTurnColor(this.chess),
      thinking: this.thinking,
      reviewingMove: this.reviewingMove,
    });

    // Engine reply first — cancel review/eval work so Stockfish can play immediately.
    if (engineToPlay) {
      this.reviewGeneration++;
      this.setReviewingMove(false);
      this.container?.querySelector('#chess-move-review')?.classList.add('hidden');
      this.engine.cancelPendingSearches();
      await this.engineTurn();
    }

    if (gameOver || !engineToPlay) {
      void this.requestEvaluation();
    }

    if (ctx && this.shouldReviewMove(ctx.lastMove.color) && this.playMode !== 'local') {
      this.scheduleMoveReview({ ...ctx, fenAfterUser });
    }
  }

  /** Run move review after the engine reply so analysis cannot block opponent moves. */
  private scheduleMoveReview(ctx: {
    fenBefore: string;
    fenAfterUser: string;
    lastMove: Move;
  }): void {
    const reviewGen = ++this.reviewGeneration;
    logChessDebug('review-scheduled', { reviewGen });
    window.setTimeout(() => {
      if (reviewGen !== this.reviewGeneration || !this.container) return;
      this.setReviewingMove(true);
      void this.reviewPlayedMove(ctx, reviewGen);
    }, 120);
  }

  private shouldReviewMove(moverColor: 'w' | 'b'): boolean {
    const human = this.humanColor();
    if (!human) return true;
    const humanCode = human === 'white' ? 'w' : 'b';
    return moverColor === humanCode;
  }

  private async reviewPlayedMove(
    ctx: {
      fenBefore: string;
      fenAfterUser: string;
      lastMove: Move;
    },
    reviewGen: number
  ): Promise<void> {
    const reviewEl = this.container?.querySelector<HTMLElement>('#chess-move-review');
    if (!reviewEl) return;

    logChessDebug('review-start', { reviewGen });
    reviewEl.classList.remove('hidden');
    reviewEl.className = 'chess-move-review chess-move-review--checking';
    reviewEl.textContent = 'Checking your move…';

    const mover = ctx.lastMove.color;
    const playedUci = verboseMoveToUci(ctx.lastMove);

    try {
      const [evalBefore, evalAfter, bestUci] = await Promise.all([
        this.engine.evaluate(ctx.fenBefore, { movetimeMs: CHESS_EVAL_REVIEW_AFTER_MS }),
        this.engine.evaluate(ctx.fenAfterUser, { movetimeMs: CHESS_EVAL_REVIEW_AFTER_MS }),
        this.engine.bestMove(ctx.fenBefore, {
          skillLevel: 10,
          movetimeMs: CHESS_EVAL_REVIEW_BEST_MS,
        }),
      ]);

      if (reviewGen !== this.reviewGeneration) {
        logChessDebug('review-abort', { reviewGen, reason: 'stale-gen' });
        reviewEl.classList.add('hidden');
        return;
      }

      if (!evalBefore || !evalAfter) {
        reviewEl.classList.add('hidden');
        return;
      }

      const result = buildMoveReviewResult({
        evalBefore,
        evalAfter,
        mover,
        playedUci,
        bestUci,
      });

      if (!result) {
        reviewEl.textContent = 'Move played — position is decisive (mate).';
        reviewEl.className = 'chess-move-review chess-move-review--good';
        return;
      }

      this.showMoveReview(result, ctx.fenBefore);
      this.setEvalBar(evalAfter);
    } catch {
      reviewEl.classList.add('hidden');
    } finally {
      if (reviewGen === this.reviewGeneration) {
        this.setReviewingMove(false);
        logChessDebug('review-done', { reviewGen });
      }
    }
  }

  private showMoveReview(result: MoveReviewResult, fenBefore: string): void {
    const reviewEl = this.container?.querySelector<HTMLElement>('#chess-move-review');
    if (!reviewEl) return;
    const label = moveReviewLabelText(result.label);
    const bestSan = uciBestMoveSan(fenBefore, result.bestUci);
    const extra =
      bestSan && !result.matchedBest
        ? ` Engine would play ${escapeHtml(bestSan)}.`
        : '';
    reviewEl.className = `chess-move-review chess-move-review--${result.label}`;
    reviewEl.innerHTML = `<strong>${escapeHtml(label)}</strong> — ${escapeHtml(result.summary)}${extra}`;
  }

  private async explainPosition(): Promise<void> {
    if (this.coachBusy) return;
    if (!loadCursorAgentPreferences().apiKey.trim()) {
      await appAlert('Add your Cursor API key in Tools → Config.', 'Explain position');
      return;
    }
    const turn = chessTurnColor(this.chess);
    const prompt = buildExplainPositionPrompt({
      fen: this.chess.fen(),
      pgn: this.chess.pgn(),
      turnLabel: turn === 'white' ? 'White' : 'Black',
    });

    this.coachBusy = true;
    openChessCoachResultModal('Explain position', coachLoadingHtml('Sending position to coach…'));
    try {
      const reply = await askChessCoach(prompt, status => {
        setChessCoachResultBody(coachLoadingHtml(status));
      });
      setChessCoachResultBody(coachReplyHtml(reply));
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Coach request failed.';
      setChessCoachResultBody(coachReplyHtml(msg));
    } finally {
      this.coachBusy = false;
    }
  }

  private async analyzeGame(): Promise<void> {
    if (this.coachBusy) return;
    if (!loadCursorAgentPreferences().apiKey.trim()) {
      await appAlert('Add your Cursor API key in Tools → Config.', 'Analyze game');
      return;
    }
    const input = await promptChessAnalyzeGame();
    if (!input) return;
    await this.runGameAnalysis({
      pgn: input.pgn,
      playerColor: input.playerColor,
    });
  }

  private async analyzeLichessGame(gameId: string): Promise<void> {
    if (this.coachBusy) return;
    if (!loadCursorAgentPreferences().apiKey.trim()) {
      await appAlert('Add your Cursor API key in Tools → Config.', 'Analyze game');
      return;
    }
    const summary = this.lichessSummaries.get(gameId);
    if (!summary) return;
    const token = loadLichessPreferences().apiToken.trim();
    if (!token) {
      await appAlert('Add your Lichess API token in Tools → Config.', 'Lichess');
      return;
    }
    try {
      const pgn = await this.resolveLichessPgn(gameId, token);
      await this.runGameAnalysis({
        pgn,
        playerColor: summary.myColor,
        headline: summary.headline,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not load game PGN.';
      await appAlert(msg, 'Lichess');
    }
  }

  private async runGameAnalysis(opts: {
    pgn: string;
    playerColor: ChessPlayerColor | null;
    headline?: string;
  }): Promise<void> {
    const prompt = buildAnalyzeGamePrompt(opts.pgn);
    this.coachBusy = true;
    openChessCoachResultModal('Analyze game', coachLoadingHtml('Sending PGN to coach…'));
    try {
      const reply = await askChessCoach(prompt, status => {
        setChessCoachResultBody(coachLoadingHtml(status));
      });
      let journalNote = '';
      if (this.deps) {
        try {
          await this.deps.saveAnalyzedGame({
            pgn: opts.pgn,
            analysis: reply,
            playerColor: opts.playerColor,
            ...(opts.headline ? { headline: opts.headline } : {}),
          });
          this.deps.onJournalSaved?.();
          journalNote = escapeHtml(`Saved to ${this.deps.chessGamesPath}.`);
        } catch (saveErr) {
          const msg = saveErr instanceof Error ? saveErr.message : 'Could not save to chess journal.';
          journalNote = escapeHtml(`Note: ${msg}`);
        }
      }
      setChessCoachResultBody(coachReplyHtml(reply, journalNote || undefined));
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Coach request failed.';
      setChessCoachResultBody(coachReplyHtml(msg));
    } finally {
      this.coachBusy = false;
    }
  }

  private async engineTurn(): Promise<void> {
    if (this.playMode === 'local' || this.chess.isGameOver()) {
      logChessDebug('engine-turn-skip', { reason: 'local-or-over' });
      return;
    }
    if (this.thinking) {
      logChessDebug('engine-turn-skip', { reason: 'already-thinking' });
      return;
    }
    const human = this.humanColor();
    const turn = chessTurnColor(this.chess);
    if (human && turn === human) {
      logChessDebug('engine-turn-skip', { reason: 'human-turn', turn });
      return;
    }

    const turnGen = ++this.engineGeneration;
    this.evalGeneration++;
    this.engine.cancelPendingSearches();
    this.thinking = true;
    this.setThinkingVisible(true);
    this.syncGround();
    logChessDebug('engine-turn-start', { turnGen, fen: this.chess.fen() });

    try {
      const fen = this.chess.fen();
      const movetimes = [
        400 + this.skillLevel * 80,
        1200,
        2800,
      ];
      let uci: string | null = null;
      for (const movetimeMs of movetimes) {
        uci = await this.engine.bestMove(fen, {
          skillLevel: this.skillLevel,
          movetimeMs,
        });
        logChessDebug('engine-bestmove', { turnGen, movetimeMs, uci: uci ?? null });
        if (uci) break;
        if (turnGen !== this.engineGeneration || !this.container) {
          logChessDebug('engine-turn-abort', { turnGen, reason: 'stale-gen' });
          return;
        }
      }
      if (turnGen !== this.engineGeneration || !this.container) return;
      if (!uci) {
        this.setStatus('Engine did not reply — try Undo or New game.');
        logChessDebug('engine-turn-fail', { turnGen, reason: 'no-uci' });
        return;
      }
      const { from, to, promotion } = uciToMove(uci);
      const applied = this.chess.move(
        promotion ? { from, to, promotion } : { from, to }
      );
      if (!applied) {
        this.setStatus('Engine played an illegal move — try Undo or New game.');
        logChessDebug('engine-turn-fail', { turnGen, reason: 'illegal-uci', uci });
        return;
      }
      if (turnGen !== this.engineGeneration) return;
      this.syncGround();
      this.updateUi();
      void this.requestEvaluation();
      logChessDebug('engine-turn-ok', { turnGen, san: applied.san });
    } catch (err) {
      if (turnGen === this.engineGeneration) {
        this.setStatus(`Engine error: ${(err as Error).message}`);
      }
      logChessDebug('engine-turn-error', {
        turnGen,
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      if (turnGen === this.engineGeneration) {
        this.thinking = false;
        this.setThinkingVisible(false);
        this.syncGround();
        this.updateUi();
      } else if (this.thinking) {
        // Stale turn was canceled — avoid leaving thinking stuck true.
        this.thinking = false;
        this.setThinkingVisible(false);
        logChessDebug('engine-turn-stale-finally', {
          turnGen,
          engineGeneration: this.engineGeneration,
        });
      }
    }
  }

  private newGame(): void {
    this.clearReplayUi();
    this.engineGeneration++;
    this.thinking = false;
    this.reviewingMove = false;
    this.reviewGeneration++;
    this.pendingPromotion = null;
    this.hidePromotion();
    this.container?.querySelector('#chess-move-review')?.classList.add('hidden');
    this.chess = new Chess();
    this.syncOrientationForMode();
    this.syncGround();
    this.updateUi();
    this.setEvalBar({ kind: 'cp', value: 0 });
    void this.requestEvaluation();
    if (this.engineMovesFirst()) void this.engineTurn();
  }

  private undo(): void {
    if (this.boardMode === 'replay') return;
    this.cancelActiveSearches();
    this.hidePromotion();
    if (this.playMode === 'local') {
      this.chess.undo();
    } else {
      this.chess.undo();
      if (!this.chess.isGameOver() && chessTurnColor(this.chess) !== this.humanColor()) {
        this.chess.undo();
      }
    }
    this.syncGround();
    this.updateUi();
    void this.requestEvaluation();
    if (this.shouldEnginePlay()) {
      void this.engineTurn();
    }
  }

  private setReviewingMove(on: boolean): void {
    if (this.reviewingMove === on) return;
    this.reviewingMove = on;
    this.syncGround();
    this.updateUi();
  }

  private flipBoard(): void {
    this.orientation = oppositeColor(this.orientation);
    this.ground?.set({ orientation: this.orientation });
    if (this.lastEvalScore) this.setEvalBar(this.lastEvalScore);
  }

  private copyPgn(): void {
    const btn = this.container?.querySelector<HTMLButtonElement>('#chess-copy-pgn');
    const pgn = this.chess.pgn();
    void navigator.clipboard?.writeText(pgn).then(
      () => {
        if (btn) flashCopyOnButton(btn, 'Copied PGN');
      },
      () => this.setStatus('Could not copy PGN.')
    );
  }

  private async requestEvaluation(): Promise<void> {
    if (!this.container || this.chess.isGameOver()) return;
    const gen = ++this.evalGeneration;
    const fen = this.chess.fen();

    try {
      const score = await this.engine.evaluate(fen, {
        movetimeMs: CHESS_EVAL_BAR_MOVETIME_MS,
        onUpdate: s => {
          if (gen === this.evalGeneration) this.setEvalBar(s);
        },
      });
      if (gen === this.evalGeneration && score) this.setEvalBar(score);
    } catch {
      /* ignore eval errors */
    }
  }

  private setEvalBar(score: EvalScore): void {
    this.lastEvalScore = score;
    const fill = this.container?.querySelector<HTMLElement>('#chess-eval-fill');
    const label = this.container?.querySelector<HTMLElement>('#chess-eval-label');
    const { displayScore, heightPct, fillFromTop } = evalBarPresentation(score, this.orientation);
    if (fill) {
      fill.style.height = `${heightPct}%`;
      fill.classList.toggle('chess-eval-fill--from-top', fillFromTop);
    }
    if (label) label.textContent = formatEvalScore(displayScore);
  }

  private updateUi(): void {
    if (this.boardMode === 'replay') {
      this.setStatus('Replay');
      this.updateReplayToolbar();
    } else {
      this.setStatus(gameStatusText(this.chess));
    }
    const movesEl = this.container?.querySelector<HTMLElement>('#chess-moves');
    if (movesEl) {
      const lines = formatMoveList(this.chess);
      movesEl.innerHTML = lines.length
        ? lines.map(m => `<li>${escapeHtml(m)}</li>`).join('')
        : '<li class="chess-moves-empty">No moves yet</li>';
    }
    const undo = this.container?.querySelector<HTMLButtonElement>('#chess-undo');
    if (undo) {
      undo.disabled =
        this.boardMode === 'replay' || this.chess.history().length === 0 || this.thinking;
    }
  }

  private setStatus(text: string): void {
    const el = this.container?.querySelector<HTMLElement>('#chess-status');
    if (el) el.textContent = text;
  }

  private setThinkingVisible(on: boolean): void {
    this.container?.querySelector('#chess-thinking')?.classList.toggle('hidden', !on);
  }

  private showPromotion(): void {
    this.container?.querySelector('#chess-promotion')?.classList.remove('hidden');
  }

  private hidePromotion(): void {
    this.pendingPromotion = null;
    this.container?.querySelector('#chess-promotion')?.classList.add('hidden');
  }

  private async refreshLichessGames(): Promise<void> {
    const listEl = this.container?.querySelector<HTMLElement>('#chess-lichess-list');
    const hintEl = this.container?.querySelector<HTMLElement>('#chess-lichess-hint');
    if (!listEl) return;

    const prefs = loadLichessPreferences();
    const token = prefs.apiToken.trim();
    if (!token) {
      if (hintEl) hintEl.textContent = 'Add your Lichess token in Tools → Config.';
      listEl.innerHTML = '';
      return;
    }

    listEl.innerHTML = `<li class="chess-lichess-loading">${escapeHtml('Loading games…')}</li>`;
    try {
      let username = prefs.username.trim();
      if (!username) {
        const account = await fetchLichessAccount(token);
        username = account.username;
        this.lichessUserId = account.id;
      } else {
        const account = await fetchLichessAccount(token);
        this.lichessUserId = account.id;
      }

      const games = await fetchRecentLichessGames(token, username, 10);
      const myId = this.lichessUserId ?? '';
      this.lichessSummaries.clear();
      this.lichessPgns.clear();
      const rows = games.map(g => {
        const summary = summarizeLichessGame(g, myId);
        this.lichessSummaries.set(g.id, summary);
        const embedded = pgnFromLichessGame(g);
        if (embedded) this.lichessPgns.set(g.id, embedded);
        const outcomeClass = `chess-lichess-outcome--${summary.outcome}`;
        return `<li class="chess-lichess-item">
          <span class="chess-lichess-label ${outcomeClass}">${escapeHtml(summary.listLabel)}</span>
          <span class="chess-lichess-actions">
            <button type="button" class="btn-ghost-dark btn-sm" data-lichess-action="load" data-game-id="${escapeHtml(g.id)}">Load</button>
            <button type="button" class="btn-ghost-dark btn-sm" data-lichess-action="analyze" data-game-id="${escapeHtml(g.id)}">Analyze</button>
          </span>
        </li>`;
      });
      listEl.innerHTML = rows.length
        ? rows.join('')
        : `<li class="chess-lichess-empty">${escapeHtml('No recent games found.')}</li>`;
      if (hintEl) hintEl.textContent = `Signed in as ${username}. Showing last ${games.length} game(s).`;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not load Lichess games.';
      listEl.innerHTML = '';
      if (hintEl) hintEl.textContent = msg;
    }
  }

  private async loadLichessGame(gameId: string): Promise<void> {
    const summary = this.lichessSummaries.get(gameId);
    const token = loadLichessPreferences().apiToken.trim();
    if (!token) {
      await appAlert('Add your Lichess API token in Tools → Config.', 'Lichess');
      return;
    }
    try {
      const pgn = await this.resolveLichessPgn(gameId, token);
      const moves = sanMovesFromPgn(pgn);
      if (!moves.length) {
        await appAlert('Could not parse moves from this PGN.', 'Load game');
        return;
      }
      this.enterReplayMode(pgn, moves, summary?.listLabel ?? 'Lichess game', summary?.myColor ?? null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not load game.';
      await appAlert(msg, 'Load game');
    }
  }

  /** PGN from games-list cache, else proxied /game/export (CORS-safe). */
  private async resolveLichessPgn(gameId: string, token: string): Promise<string> {
    const cached = this.lichessPgns.get(gameId)?.trim();
    if (cached) return cached;
    const pgn = await fetchLichessGamePgn(token, gameId);
    this.lichessPgns.set(gameId, pgn);
    return pgn;
  }

  private enterReplayMode(
    pgn: string,
    moves: string[],
    title: string,
    myColor: ChessPlayerColor | null
  ): void {
    this.boardMode = 'replay';
    this.replayMoves = moves;
    this.replayIndex = 0;
    this.replayTitle = title;
    this.thinking = false;
    this.pendingPromotion = null;
    this.hidePromotion();
    this.container?.querySelector('#chess-move-review')?.classList.add('hidden');

    if (myColor === 'white') this.orientation = 'white';
    else if (myColor === 'black') this.orientation = 'black';
    this.ground?.set({ orientation: this.orientation });

    this.container?.querySelector('#chess-replay-toolbar')?.classList.remove('hidden');
    const modeSelect = this.container?.querySelector<HTMLSelectElement>('#chess-mode');
    if (modeSelect) modeSelect.disabled = true;

    this.applyReplayPosition();
    void this.requestEvaluation();
  }

  private clearReplayUi(): void {
    if (this.boardMode !== 'replay') return;
    this.boardMode = 'play';
    this.replayMoves = [];
    this.replayIndex = 0;
    this.replayTitle = '';
    this.container?.querySelector('#chess-replay-toolbar')?.classList.add('hidden');
    const modeSelect = this.container?.querySelector<HTMLSelectElement>('#chess-mode');
    if (modeSelect) modeSelect.disabled = false;
  }

  private exitReplayMode(): void {
    this.clearReplayUi();
    this.thinking = false;
    this.pendingPromotion = null;
    this.hidePromotion();
    this.chess = new Chess();
    this.syncOrientationForMode();
    this.syncGround();
    this.updateUi();
    this.setEvalBar({ kind: 'cp', value: 0 });
    void this.requestEvaluation();
    if (this.engineMovesFirst()) void this.engineTurn();
  }

  private setReplayIndex(index: number): void {
    if (this.boardMode !== 'replay') return;
    this.replayIndex = Math.max(0, Math.min(index, this.replayMoves.length));
    this.applyReplayPosition();
  }

  private applyReplayPosition(): void {
    this.chess = positionAfterMoves(this.replayMoves, this.replayIndex);
    this.syncGround();
    this.updateUi();
    this.updateReplayToolbar();
    void this.requestEvaluation();
  }

  private updateReplayToolbar(): void {
    const metaEl = this.container?.querySelector<HTMLElement>('#chess-replay-meta');
    const plyEl = this.container?.querySelector<HTMLElement>('#chess-replay-ply');
    if (!plyEl) return;
    const total = this.replayMoves.length;
    if (metaEl) metaEl.textContent = this.replayTitle || 'Loaded game';
    plyEl.textContent = total === 0 ? 'No moves' : `Move ${this.replayIndex} / ${total}`;
    const start = this.container?.querySelector<HTMLButtonElement>('#chess-replay-start');
    const prev = this.container?.querySelector<HTMLButtonElement>('#chess-replay-prev');
    const next = this.container?.querySelector<HTMLButtonElement>('#chess-replay-next');
    const end = this.container?.querySelector<HTMLButtonElement>('#chess-replay-end');
    if (start) start.disabled = this.replayIndex <= 0;
    if (prev) prev.disabled = this.replayIndex <= 0;
    if (next) next.disabled = this.replayIndex >= total;
    if (end) end.disabled = this.replayIndex >= total;
  }
}
